import { AdTrackingPlugin } from '@snowplow/browser-plugin-ad-tracking';
import { ClientHintsPlugin } from '@snowplow/browser-plugin-client-hints';
import {
  enableLinkClickTracking,
  LinkClickTrackingPlugin,
} from '@snowplow/browser-plugin-link-click-tracking';
import { PerformanceTimingPlugin } from '@snowplow/browser-plugin-performance-timing';
import type {
  BrowserPlugin,
  BrowserTracker,
  ConditionalContextProvider,
  ContextPrimitive,
} from '@snowplow/browser-tracker';
import {
  addGlobalContexts,
  crossDomainLinker,
  newTracker,
  trackPageView,
} from '@snowplow/browser-tracker';

import { getCollector, getRootDomain, isLocalHostname } from '../url/url';
import type { CrossDomainLinkerCriterion } from './cross-domain-linker-criterion';
import { linksToHcDomainCriterion } from './cross-domain-linker-criterion';

interface InitSnowPlowTracker {
  isProd?: boolean;
  plugins?: BrowserPlugin[];
  crossDomainLinkerCriterion?: CrossDomainLinkerCriterion | null;
  contexts?: Array<ConditionalContextProvider | ContextPrimitive> | null;
}

/**
 * This wrapper type is only for patching the `getDomainUserId` method that is incorrectly typed in the `@snowplow/browser-tracker` package.
 * The type is mentioned in https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/web-tracker/advanced-usage/tracker-information/?platform=browser#getdomainuserinfo
 * but the implementation is different and it was imported from https://github.com/snowplow/snowplow-javascript-tracker/blob/1df5e9f48411720a6449a8882665e490a58cbdbf/libraries/browser-tracker-core/src/tracker/id_cookie.ts#L50
 *
 * @internal
 */
export type ParsedIdCookie = [
  cookieDisabled: string,
  domainUserId: string,
  cookieCreateTs: number,
  visitCount: number,
  nowTs: number,
  lastVisitTs: number | undefined,
  sessionId: string,
  previousSessionId: string,
  firstEventId: string,
  firstEventTs: number | undefined,
  eventIndex: number,
];

export interface TypedBrowserTracker
  extends Omit<BrowserTracker, 'getUserId' | 'getDomainUserInfo'> {
  getUserId(): string;
  getSessionId(): string;
  getDomainUserId(): string;
  getDomainUserInfo(): ParsedIdCookie;
}

export interface ResponseSnowPlow {
  tracker: TypedBrowserTracker;
  pageView: typeof trackPageView;
}

/**
 * Initialize the tracker
 * v.0.14.0;
 * @public
 *
 * @param config - SnowPlow configuration
 * @returns A Snowplow tracker
 *
 * @example
 * ```ts
 *
 * import { initSnowplowTracker, getHealthcareCollector } from '@hc-frontend/third-party-snowplow';
 *
 * initSnowplowTracker({
 *   appId: 'healthcare.com',
 *   collector: getHealthcareCollector({ isProd: true }),
 *   crossDomainLinkerCriterion: linkElement => linkElement.getAttribute("data-is-cross-domain") === "true"
 *   contexts: [{ schema: "iglu:com.healthcare/context_schema/jsonschema/1-0-0", data: { key: "value" } }]
 * });
 * ```
 */
function initSnowplowTracker({
  isProd = false,
  plugins = [],
  crossDomainLinkerCriterion = linksToHcDomainCriterion,
  contexts = [],
}: InitSnowPlowTracker): ResponseSnowPlow {
  const currentPlugins = [
    LinkClickTrackingPlugin(),
    PerformanceTimingPlugin(),
    ClientHintsPlugin(),
    AdTrackingPlugin(),
  ];

  const appId = getRootDomain();
  const collector = getCollector(window.location.hostname, isProd);

  const tracker = newTracker('sp', collector, {
    appId,
    discoverRootDomain: true,
    cookieSecure: !isLocalHostname(window.location.hostname),
    cookieSameSite: 'Lax',
    plugins: currentPlugins.concat(plugins),
    keepalive: true,
  });

  enableLinkClickTracking({ pseudoClicks: true, trackContent: true });
  if (crossDomainLinkerCriterion != null)
    crossDomainLinker(crossDomainLinkerCriterion);

  if (contexts != null && contexts.length > 0) addGlobalContexts(contexts);

  return { tracker: tracker as TypedBrowserTracker, pageView: trackPageView };
}

export { initSnowplowTracker };
